import React from 'react';
import { toast } from 'react-toastify';
import { useRouter } from 'next/router';
import { createUserWithEmailAndPassword, UserCredential } from 'firebase/auth';
import {
  addDoc,
  collection,
  doc,
  setDoc,
  Timestamp,
  updateDoc,
} from 'firebase/firestore';
import { setUserId, logEvent } from 'firebase/analytics';
import { z, ZodError } from 'zod';
import { FirebaseError } from 'firebase/app';
import posthog from 'posthog-js';
import { auth, firestore, loadAnalytics } from '../../../lib';
import { Button } from '../../commons';
import { Input } from '../../inputs';
import { useFirebaseUser, useUser } from '../../../hooks';
import { signUpSchema, User } from '../../../models';

const toastError = (message: string) =>
  toast.error(message, {
    position: 'top-right',
    autoClose: 5000,
    hideProgressBar: false,
    closeOnClick: true,
    pauseOnHover: true,
    draggable: true,
    progress: undefined,
  });

export default function SignUpForm() {
  const [, setUser] = useUser();
  const [, setFbUser] = useFirebaseUser();
  const router = useRouter();
  const onSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const formData = new FormData(e.currentTarget);
    let data: z.infer<typeof signUpSchema> = null;
    try {
      data = signUpSchema.parse(Object.fromEntries(formData.entries()));
    } catch (err) {
      if (err instanceof ZodError) {
        return toastError(err.issues[0].message);
      }
      return toastError(
        'An error has occurred, please try again later. CODE: E11b'
      );
    }
    let credential: UserCredential = null;
    try {
      credential = await createUserWithEmailAndPassword(
        auth,
        data.email.toLowerCase(),
        data.password
      );
    } catch (err) {
      if (err instanceof FirebaseError) {
        let msg = '';
        if (err.code === 'auth/email-already-in-use') {
          msg = 'The email address is already. Try signing in instead.';
        }
        return toastError(msg);
      }
      return toastError(
        'An error has occurred, please try again later. CODE: E12b'
      );
    }
    try {
      const application = await addDoc(collection(firestore, 'applications'), {
        user: credential.user.uid,
        startDate: Timestamp.fromDate(new Date()),
        step: '1',
        status: 'progress',
        archive: false,
      });

      await updateDoc(doc(firestore, 'applications', application?.id), {
        id: application?.id,
      });

      await setDoc(doc(firestore, 'users', credential.user.uid), {
        id: credential.user.uid,
        email: formData.get('email')?.toString()?.trim().toLowerCase(),
        firstName: formData.get('firstName')?.toString()?.trim(),
        lastName: formData.get('lastName')?.toString()?.trim(),
        application: application?.id,
        privilege: 'applicant',
        createdAt: Timestamp.fromDate(new Date()),
      });
      data.password = undefined;
      setUser({
        id: credential.user.uid,
        ...data,
        application: application?.id,
        privilege: 'applicant',
        createdAt: new Date(),
      } as User);
      setFbUser(credential.user);
      setUserId(loadAnalytics(), credential.user.uid);
      logEvent(loadAnalytics(), 'sign_up', {
        method: 'email',
      });
      posthog?.identify(credential.user.uid, {
        id: credential.user.uid,
        ...data,
        application: application?.id,
        privilege: 'applicant',
        createdAt: new Date(),
      });
      await router.push(
        '/request-scholarship/[...id]',
        `/request-scholarship/2`
      );
    } catch (err) {
      posthog?.captureException(err);
      return toastError(
        'An error has occurred, please try again later. CODE: E13b'
      );
    }
  };
  return (
    <form method="POST" onSubmit={onSubmit}>
      <div className="grid grid-cols-2 grid-rows-2 gap-4">
        <Input
          type="email"
          name="email"
          label="Email"
          placeholder="Email"
          required
        />
        <Input
          type="password"
          name="password"
          label="Password"
          placeholder="Password"
          required
        />
        <Input
          type="text"
          name="firstName"
          label="First Name"
          placeholder="First Name"
          required
        />
        <Input
          type="text"
          name="lastName"
          label="Last Name"
          placeholder="Last Name"
          required
        />
      </div>
      <Button type="submit" color="blue" className="text-center w-full mt-5">
        Sign Up
      </Button>
    </form>
  );
}
